/**
 * REGION SELECT — Autocomplete delle Region per il country specificato,
 * lazy load all'arrivo del countryId (fetch idempotente per countryId).
 *
 * Dipendente dal CountrySelect del parent: il parent passa `countryId`
 * (selezione corrente) come prop, e il componente risponde:
 *   - countryId null/undefined → disabled, options vuote
 *   - countryId valido → useEffect dispatcha fetchRegionsForCountry, che
 *     ha `condition` per skip "already loading/succeeded" (cache hit)
 *
 * Il **reset del valore region quando il country cambia** è gestito dal
 * parent (deve passare `value=null` insieme al nuovo `countryId`).
 *
 * Modalità `inlineEditable`: simmetrica a CountrySelect — view-mode flat
 * (label) + edit on double-click. Per detail page inline.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchRegionsForCountry } from '../entities/geography/geographyThunks';
import type { Region } from '../entities/geography/geographyTypes';

export interface RegionSelectProps {
  value: number | null | undefined;
  onChange: (regionId: number | null) => void;
  /** Country corrente; se null/undefined il select è disabled. */
  countryId: number | null | undefined;
  /** Default: t('address.region') */
  label?: string;
  required?: boolean;
  fullWidth?: boolean;
  className?: string;
  /** Forza disabled anche con countryId valido (es. parent in saving state). */
  disabled?: boolean;
  /**
   * Se true, view-mode flat (label) + edit on double-click. Coerente con
   * EditableField. Per l'uso inline in detail page (es. AddressListEditor).
   */
  inlineEditable?: boolean;
}

export function RegionSelect({
  value,
  onChange,
  countryId,
  label,
  required,
  fullWidth,
  className,
  disabled,
  inlineEditable,
}: RegionSelectProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const regions = useAppSelector((s) =>
    countryId != null ? s.geography.regionsByCountry[countryId] ?? [] : [],
  );
  const status = useAppSelector((s) =>
    countryId != null ? s.geography.regionsStatus[countryId] : undefined,
  );
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (countryId != null) dispatch(fetchRegionsForCountry(countryId));
  }, [dispatch, countryId]);

  const selected: Region | null = useMemo(() => {
    if (value == null) return null;
    return regions.find((r) => r.regionId === value) ?? null;
  }, [regions, value]);

  // Disabled se manca countryId (niente da scegliere) o se il parent forza.
  const isDisabled = disabled || countryId == null;

  // Inline editable: in view mode mostra solo la label (flat) + double click
  // per entrare in edit. Se isDisabled non si entra in edit (no country
  // selezionato → niente regions disponibili).
  if (inlineEditable && !editing) {
    return (
      <div
        className={`editable-field editable-field--view editable-field--flat ${className ?? ''}`}
        role={isDisabled ? undefined : 'button'}
        tabIndex={isDisabled ? -1 : 0}
        onDoubleClick={() => { if (!isDisabled) setEditing(true); }}
        onKeyDown={(e) => {
          if (isDisabled) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(true); }
        }}
        title={isDisabled ? undefined : t('editableField.doubleClickToEdit')}
      >
        <span className="editable-field__value">{selected?.title ?? '—'}</span>
      </div>
    );
  }

  return (
    <Autocomplete
      options={regions}
      value={selected}
      onChange={(_, next) => {
        onChange(next?.regionId ?? null);
        if (inlineEditable) setEditing(false);
      }}
      onBlur={() => { if (inlineEditable) setEditing(false); }}
      getOptionLabel={(opt) => opt.title}
      isOptionEqualToValue={(a, b) => a.regionId === b.regionId}
      loading={status === 'loading'}
      disabled={isDisabled}
      fullWidth={fullWidth}
      openOnFocus={inlineEditable}
      className={className}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={inlineEditable}
          label={label ?? t('address.region')}
          required={required}
        />
      )}
    />
  );
}
